import i18next from "i18next";
import ROUTES from "../../config/Routes";
import { EADataGridCell } from "../common/datatable/EADataGrid";
import { formatDate } from "../../services/common/Utils";
import DataTableActions from "../common/datatable/DataTableActions";
import MainHeaderCell from "../common/datatable/MainHeaderCell";
import React from "react";
import { getAttributeValueDisplay, getAttributeValueOptions, sortAttributeMultipleListValue } from "../../services/common/AttributeUtils";
import { PrivilegeEnum } from "services/common/RolesUtils";
import { AttributeType, isSealedDossier, statusEngCode } from 'services/common/Constants';
import DossierStatusChip from "components/common/DossierStatusChip";
import { getResultColorCodeById } from '../../services/common/DataGridUtils';

const getDossierUrl = (accountId, dossierId, monitored, component, thirdpartyId) => {
  let route = monitored ? ROUTES : ROUTES.ARCHIVES;
  if(component === "thirdparty") {
    route = route.THIRDPARTY_DOSSIER_DETAIL.replace(":thirdpartyId", thirdpartyId).replace(":dossierId", dossierId)
  } else {
    route = route.DOSSIER_DETAIL.replace(":dossierId", dossierId);
  }
  return `/${accountId}/${route}`;
}

export const initialDossierTableSort = (component) => [
  {
    field:
      component === "dossiers-list" ? "thirdpartyName" : "dossierReference",
    sort: "asc",
  },
];

export const dossiersDefaultVisibilityColumns = (
  attributesColumns,
  calculatedAttributesColumns,
  indicatorsColumns,
  monitored
) => {
  return {
    //dossiers list
    thirdpartyCode: false,
    thirdpartyIdentifier: false,
    duns: false,
    vat: false,

    //Common
    creationDate: false,
    endDate: !monitored,
    statusCode: monitored,

    ...attributesColumns?.reduce(
      (res, attr) => ({
        ...res,
        [`attribute-${attr.label}`]: false,
      }),
      {}
    ),
    ...indicatorsColumns?.reduce(
      (res, indic) => ({
        ...res,
        [`indicator-${indic.label}`]: false,
      }),
      {}
    ),
    ...calculatedAttributesColumns?.reduce(
      (res, attr) => ({
        ...res,
        [`calculatedAttribute-${attr.label}`]: false,
      }),
      {}
    )
  };
};

const dossiersDataTableActions = (
  accountId,
  dossier,
  history,
  monitored,
  onStopDossier,
  onReactivateDossier,
  hasAnyOfPrivilegesOnScopedResourceImpl,
  component,
  thirdpartyId,
  dossierTypeLibelles,
  contextThirdparty
) => {
  const actions = [
    {
      label: i18next.t("show"),
      icon: "modify-action",
      isDisabled: false,
      action: () => history.push(getDossierUrl(accountId, dossier.id, monitored, component, thirdpartyId)),
    },
  ];
  if (monitored) {
    actions.push({
      label: i18next.t("stop"),
      icon: "stop-action",
      isDisabled: !hasAnyOfPrivilegesOnScopedResourceImpl(dossier, PrivilegeEnum.PRIVILEGE_ARCHIVE_DOSSIER) || isSealedDossier(dossier.dossierTypeCode),
      action: () => onStopDossier(dossier.id),
    });
  }
  if (!monitored) {
    actions.push(    {
      label: i18next.t("reactivate"),
      icon: "play_circle_filled_green",
      isDisabled: ![...dossierTypeLibelles, 'Libre'].includes(dossier.dossierTypeLibelle) ||
        !dossier.thirdpartyActive ||
        isSealedDossier(dossier.dossierTypeCode) ||
        (contextThirdparty && contextThirdparty.isArchived),
      action: () => onReactivateDossier(dossier.id, dossier),
    },);
  }
  return actions;
};

export const getDossiersDataTableColumns = (
  component,
  accountId,
  monitored,
  dossiersTypesLabels,
  attributesColumns,
  calculatedAttributesColumns,
  indicatorsColumns,
  onStopDossier,
  onReactivateDossier,
  history,
  hasAnyOfPrivilegesOnScopedResourceImpl,
  dossierTypeLibelles,
  sortModel,
  contextThirdparty
) => {

  const isDossiersList = component === "dossiers-list";
  return [
    {
      field: "dossierTypeLibelle",
      headerName: i18next.t("dossierTable.columns.dossierType"),
      filterable: !isDossiersList,
      flex: isDossiersList ? 0.1 : 0.17,
      type: "singleSelect",
      valueOptions: dossiersTypesLabels,
      renderHeader: () => (
        <MainHeaderCell name={i18next.t('dossierTable.columns.dossierType')}/>
      ),
      renderCell: (params) => (
        <EADataGridCell link={getDossierUrl(accountId, params.row.id, monitored, component, params.row.thirdpartyId)}>
          {params.value}
        </EADataGridCell>
      ),
    },
    {
      field: "dossierReference",
      headerName: i18next.t("dossierTable.columns.dossierReference"),
      filterable: !isDossiersList,
      flex: isDossiersList ? 0.08 : 0.17,
      renderCell: (params) => (
        <EADataGridCell link={getDossierUrl(accountId, params.row.id, monitored, component, params.row.thirdpartyId)}>
          {params.value}
        </EADataGridCell>
      ),
    },
    {
      field: "creationDate",
      headerName: i18next.t("addedOn"),
      filterable: !isDossiersList,
      flex: isDossiersList ? 0.07 : 0.17,
      type: "date",
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <EADataGridCell
          link={getDossierUrl(accountId, params.row.id, monitored, component, params.row.thirdpartyId)}
        >
          {formatDate(params.value)}
        </EADataGridCell>
      )
    },
    {
      field: "endDate",
      headerName: i18next.t(monitored ? "endDate" : "stoppedOn"),
      filterable: !isDossiersList,
      flex: isDossiersList ? 0.07 : 0.17,
      type: "date",
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <EADataGridCell link={getDossierUrl(accountId, params.row.id, monitored, component, params.row.thirdpartyId)} >
          {formatDate(params.value)}
        </EADataGridCell>
      )
    },
    ...(isDossiersList
      ? [
          {
            field: "thirdpartyName",
            headerName: i18next.t("thirdparty"),
            filterable: false,
            flex: 0.17,
            renderCell: (params) => (
              <EADataGridCell link={getDossierUrl(accountId, params.row.id, monitored, component, params.row.thirdpartyId)}>
                {params.value}
              </EADataGridCell>
            ),
          },
          {
            field: "thirdpartyIdentifier",
            headerName: i18next.t("dossierTable.columns.thirdpartyIdentifier"),
            filterable: false,
            flex: 0.09,
            renderCell: (params) => (
              <EADataGridCell link={getDossierUrl(accountId, params.row.id, monitored, component, params.row.thirdpartyId)}>
                {params.value}
              </EADataGridCell>
            ),
          },
          {
            field: "duns",
            headerName: i18next.t("duns"),
            filterable: false,
            flex: 0.09,
            renderCell: (params) => (
              <EADataGridCell link={getDossierUrl(accountId, params.row.id, monitored, component, params.row.thirdpartyId)}>
                {params.value}
              </EADataGridCell>
            ),
          },
          {
            field: "vat",
            headerName: i18next.t("tva"),
            filterable: false,
            flex: 0.09,
            renderCell: (params) => (
              <EADataGridCell link={getDossierUrl(accountId, params.row.id, monitored, component, params.row.thirdpartyId)}>
                {params.value}
              </EADataGridCell>
            ),
          },
          {
            field: "thirdpartyCode",
            headerName: i18next.t("dossierTable.columns.thirdpartyCode"),
            filterable: false,
            flex: 0.08,
            renderCell: (params) => (
              <EADataGridCell link={getDossierUrl(accountId, params.row.id, monitored, component, params.row.thirdpartyId)}>
                {params.value}
              </EADataGridCell>
            ),
          },
        ]
      : []),
    ...(monitored
      ? [
          {
            field: "statusCode",
            headerName: i18next.t("myDocuments.status"),
            filterable: !isDossiersList,
            flex: 0.1,
            headerAlign: "center",
            align: "center",
            type: "singleSelect",
            valueOptions: [
              { value: "IN_PROGRESS", label: i18next.t(`viewer.status.statusLabelDossier.${statusEngCode.IN_PROGRESS}`) },
              { value: "ACTION_REQUIRED", label: i18next.t(`viewer.status.statusLabelDossier.${statusEngCode.ACTION_REQUIRED}`) },
              { value: "NOT_VALID", label: i18next.t(`viewer.status.statusLabelDossier.${statusEngCode.NOT_VALID}`) },
              { value: "VALID", label: i18next.t(`viewer.status.statusLabelDossier.${statusEngCode.VALID}`) },
              { value: "MISSING", label: i18next.t(`viewer.status.statusLabelDossier.${statusEngCode.MISSING}`) }
            ],
            renderCell: (params) => (
              <EADataGridCell
                link={getDossierUrl(accountId, params.row.id, monitored, component, params.row.thirdpartyId)}
                tooltipLabel={i18next.t(`viewer.status.statusLabelDossier.${params.row.statusCode}`)}
              >
                <DossierStatusChip statusCode={params.value} />
              </EADataGridCell>
            ),
          }
        ]
      : []),
    ...attributesColumns?.map((attrCol) => {
      return {
        field: `attribute-${attrCol.label}`,
        headerName: attrCol.label,
        filterable: !isDossiersList,
        renderHeader: () => <span>{attrCol.label}</span>,
        flex: 0.08,
        type: attrCol.gridColumnType,
        headerAlign: "center",
        ...(attrCol.gridColumnType === "singleSelect" && { valueOptions: getAttributeValueOptions(attrCol) }),
        renderCell: (params) => {
          let attributeValuesStr = getAttributeValueDisplay(attrCol.type, params.value);
          if(attrCol.type === AttributeType.LIST_MULTIPLE) {
            attributeValuesStr = sortAttributeMultipleListValue(attributeValuesStr, params.field, sortModel)
          }
          return (
            <EADataGridCell
              link={getDossierUrl(accountId, params.row.id, monitored, component, params.row.thirdpartyId)}
            >
              <span>
                {attributeValuesStr}
              </span>
            </EADataGridCell>
          )
        },
      };
    }),
    ...calculatedAttributesColumns?.map((attrCol) => {
      return {
        field: `calculatedAttribute-${attrCol.label}`,
        filterable: false,
        headerName: attrCol.label,
        renderHeader: () => <span>{attrCol.label}</span>,
        flex: 0.08,
        headerAlign: "center",
        type: attrCol.gridColumnType,
        renderCell: (params) => (
          <EADataGridCell
            link={getDossierUrl(accountId, params.row.id, monitored, component, params.row.thirdpartyId)}
          >
            <div
              style={{
                color: "#535362",
                fontSize: "11px",
                display: "flex",
                alignItems: "center",
                backgroundColor: getResultColorCodeById(params.row.calculatedAttributes || [], attrCol.calculationId),
                gap: "5px",
                padding: "8px",
                height: "28px",
                borderRadius: "13px"
              }}
            >
              {params.value}
            </div>
          </EADataGridCell>
        ),
      };
    }),
    ...indicatorsColumns?.map((indicCol) => {
      return {
        field: `indicator-${indicCol.label}`,
        filterable: false,
        headerName: indicCol.label,
        renderHeader: () => <span>{indicCol.label}</span>,
        flex: 0.08,
        headerAlign: "center",
        type: indicCol.gridColumnType,
        renderCell: (params) => (
          <EADataGridCell
            link={getDossierUrl(accountId, params.row.id, monitored, component, params.row.thirdpartyId)}
          >
            <div
              style={{
                color: "#535362",
                fontSize: "11px",
                display: "flex",
                alignItems: "center",
                backgroundColor: getResultColorCodeById(params.row.indicators || [], indicCol.calculationId),
                gap: "5px",
                padding: "8px",
                height: "28px",
                borderRadius: "13px"
              }}
            >
            {params.value}
            </div>
          </EADataGridCell>
        ),
      };
    }),
    {
      field: "action",
      headerName: i18next.t("actions"),
      flex: 0.05,
      sortable: false,
      filterable: false,
      disableExport: true,
      hideSortIcons: true,
      disableReorder: true,
      disableColumnMenu: true,
      disableMultipleSelection: true,
      disableSelectionOnClick: true,
      isRowSelectable: false,
      hideFooterSelectedRowCount: true,
      align: "center",
      renderCell: (params) => {
        return (
          <div style={{ textAlign: "center" }}>
            <DataTableActions
              actions={dossiersDataTableActions(
                accountId,
                params.row,
                history,
                monitored,
                onStopDossier,
                onReactivateDossier,
                hasAnyOfPrivilegesOnScopedResourceImpl,
                component,
                params.row.thirdpartyId,
                dossierTypeLibelles,
                contextThirdparty
              )}
            />
          </div>
        );
      },
    },
  ];
};
